import React, { useState, useEffect } from 'react';
import Icon from 'components/AppIcon';
import { salesService, batchesService } from '../../../services/metalguard';
import { supabase } from '../../../lib/supabase';
import { useToast } from 'components/Toast';

function FieldError({ msg }) {
  if (!msg) return null;
  return (
    <p className="text-xs mt-1" style={{ color: 'var(--color-error)', fontFamily: '"IBM Plex Sans", sans-serif' }}>{msg}</p>
  );
}

function PricingRefPanel({ batch }) {
  const [tick, setTick] = useState(null);
  const [loadingTick, setLoadingTick] = useState(true);

  useEffect(() => {
    const fetchTick = async () => {
      setLoadingTick(true);
      try {
        const metalName = batch?.metal_name;
        if (!metalName) { setLoadingTick(false); return; }
        const { data, error } = await supabase?.from('market_ticks')?.select('value, ts, symbol')?.ilike('symbol', `%${metalName}%`)?.order('ts', { ascending: false })?.limit(1)?.maybeSingle();
        if (!error && data) setTick(data);
      } catch (_) {}
      setLoadingTick(false);
    };
    fetchTick();
  }, [batch?.metal_name]);

  const benchmark = tick?.value ? parseFloat(tick?.value) : null;
  const effectiveYield = (batch?.yield_actual ?? batch?.yield_est) ? parseFloat(batch?.yield_actual ?? batch?.yield_est) / 100 : null;
  const weightT = parseFloat(batch?.weight_t) || 0;
  const recoverableValue = benchmark != null && effectiveYield != null ? benchmark * effectiveYield * weightT : null;
  const maxBuyPrice = batch?.max_buy_price ? parseFloat(batch?.max_buy_price) : null;
  const yieldDisplay = batch?.yield_actual != null ? `${batch?.yield_actual}% (real)` : batch?.yield_est != null ? `${batch?.yield_est}% (est.)` : '—';

  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
      <div className="flex items-center gap-1.5 mb-2">
        <Icon name="Info" size={13} color="#2563eb" strokeWidth={2} />
        <span className="text-xs font-semibold text-blue-700" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
          Referencia de Precio — {batch?.metal_name || 'Metal'}
        </span>
        {loadingTick && (
          <div className="ml-auto w-3 h-3 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
        )}
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
        <div>
          <p className="text-xs text-blue-500" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Benchmark actual</p>
          <p className="text-sm font-mono font-semibold text-blue-800">
            {benchmark != null ? `${benchmark?.toLocaleString('es-ES', { maximumFractionDigits: 0 })} €/t` : '—'}
          </p>
        </div>
        <div>
          <p className="text-xs text-blue-500" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Yield efectivo</p>
          <p className="text-sm font-mono font-semibold text-blue-800">{yieldDisplay}</p>
        </div>
        <div>
          <p className="text-xs text-blue-500" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Valor recuperable est.</p>
          <p className="text-sm font-mono font-semibold text-blue-800">
            {recoverableValue != null ? `${recoverableValue?.toLocaleString('es-ES', { maximumFractionDigits: 0 })} €` : '—'}
          </p>
        </div>
        <div>
          <p className="text-xs text-blue-500" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Precio máx. calculado</p>
          <p className="text-sm font-mono font-semibold text-blue-800">
            {maxBuyPrice != null ? `${maxBuyPrice?.toLocaleString('es-ES', { maximumFractionDigits: 0 })} €/t` : '—'}
          </p>
        </div>
      </div>
      {tick?.ts && (
        <p className="text-xs text-blue-400 mt-2" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
          Benchmark del {new Date(tick.ts)?.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
        </p>
      )}
      {!loadingTick && benchmark == null && (
        <p className="text-xs text-blue-400 mt-1" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
          Sin datos de benchmark disponibles para este metal.
        </p>
      )}
    </div>
  );
}

export default function MarkAsSoldModal({ batch, onClose, onSuccess }) {
  const todayStr = new Date()?.toISOString()?.split('T')?.[0];
  const { addToast } = useToast();
  const [form, setForm] = useState({
    sale_price_per_ton: '',
    actual_yield_pct: batch?.yield_est || '',
    buyer_name: '',
    notes: '',
    sold_at: todayStr,
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [savedOk, setSavedOk] = useState(false);

  const validateField = (name, value) => {
    if (name === 'sale_price_per_ton') {
      const v = parseFloat(value);
      if (!value && value !== 0) return 'El precio de venta es obligatorio y debe ser mayor que 0';
      if (isNaN(v) || v <= 0) return 'El precio de venta es obligatorio y debe ser mayor que 0';
    }
    if (name === 'actual_yield_pct') {
      if (value !== '' && value !== undefined) {
        const v = parseFloat(value);
        if (isNaN(v) || v < 0 || v > 100) return 'El rendimiento real debe estar entre 0 y 100';
      }
    }
    if (name === 'buyer_name') {
      if (!value?.trim()) return 'El nombre del comprador es obligatorio';
      if (value?.trim()?.length < 2) return 'El nombre del comprador es obligatorio (mín. 2 caracteres)';
    }
    if (name === 'notes') {
      if (value?.length > 500) return 'Máximo 500 caracteres';
    }
    if (name === 'sold_at') {
      if (!value) return 'La fecha de venta es obligatoria';
      const d = new Date(value);
      if (isNaN(d?.getTime())) return 'Fecha no válida';
      if (d > new Date()) return 'La fecha de venta no puede ser futura';
    }
    return null;
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (touched?.[field]) {
      const err = validateField(field, value);
      setErrors(prev => ({ ...prev, [field]: err }));
    } else {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    const err = validateField(field, form?.[field]);
    setErrors(prev => ({ ...prev, [field]: err }));
  };

  const validateAll = () => {
    const fields = ['sale_price_per_ton', 'actual_yield_pct', 'buyer_name', 'notes', 'sold_at'];
    const newErrors = {};
    fields?.forEach(f => {
      const err = validateField(f, form?.[f]);
      if (err) newErrors[f] = err;
    });
    setErrors(newErrors);
    setTouched(Object.fromEntries(fields?.map(f => [f, true])));
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateAll()) return;
    setLoading(true);
    try {
      const salePrice = parseFloat(form?.sale_price_per_ton) || 0;
      const weight = parseFloat(batch?.weight_t) || 0;
      const actualYield = parseFloat(form?.actual_yield_pct) || 0;
      const purchasePrice = parseFloat(batch?.purchase_price) || 0;
      const realizedMargin = salePrice - purchasePrice;
      const totalMargin = realizedMargin * weight;
      const totalSaleValue = salePrice * weight;
      const purchaseValue = parseFloat(batch?.purchase_price) * parseFloat(batch?.weight_t) || null;
      const realizedPnl = totalSaleValue - purchaseValue;

      const { error: saleError } = await salesService?.create({
        batch_id: batch?.id,
        sale_price_eur_per_ton: salePrice,
        real_yield_pct: actualYield,
        buyer_name: form?.buyer_name || null,
        buyer_notes: form?.notes || null,
        sold_at: new Date(form?.sold_at)?.toISOString(),
        realized_margin_eur_per_ton: realizedMargin,
        total_margin_eur: totalMargin,
        metal_name: batch?.metal_name,
        category_name: batch?.category_name,
        weight_t: weight,
        sale_price_per_ton: salePrice,
        actual_yield_pct: actualYield,
        total_sale_value: totalSaleValue,
        purchase_value: purchaseValue,
        realized_pnl: realizedPnl,
        notes: form?.notes || null,
        yield_est: batch?.yield_est || null,
        days_in_inventory: batch?.created_at
          ? Math.floor((new Date(form?.sold_at) - new Date(batch?.created_at)) / (1000 * 60 * 60 * 24))
          : null,
        risk_buffer: batch?.risk_buffer || 0,
        negotiated_price: batch?.negotiated_price || null,
      });
      if (saleError) throw saleError;

      const { error: batchError } = await batchesService?.update(batch?.id, {
        batch_status: 'Completado',
        actual_margin: realizedMargin,
      });
      if (batchError) throw batchError;

      setSavedOk(true);
      const metalLabel = batch?.metal_name || batch?.category_name || 'Metal';
      const weightLabel = weight ? `${weight?.toFixed(2)}t` : '';
      addToast(`Venta registrada — ${metalLabel} ${weightLabel}`?.trim(), 'success');
      setTimeout(() => {
        setSavedOk(false);
        onSuccess?.();
        onClose?.();
      }, 1200);
    } catch (err) {
      setErrors(prev => ({ ...prev, _submit: err?.message || 'Error al registrar la venta' }));
    } finally {
      setLoading(false);
    }
  };

  const weight = parseFloat(batch?.weight_t) || 0;
  const salePrice = parseFloat(form?.sale_price_per_ton) || 0;
  const purchasePrice = parseFloat(batch?.purchase_price) || 0;
  const totalSale = salePrice * weight;
  const totalPurchase = purchasePrice * weight;
  const pnl = totalSale - totalPurchase;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}>
      <div className="bg-card border border-border rounded-xl w-full max-w-lg" style={{ boxShadow: 'var(--shadow-xl)' }}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(5,150,105,0.1)' }}>
              <Icon name="ShoppingCart" size={16} color="#059669" strokeWidth={2} />
            </div>
            <div>
              <h3 className="font-semibold text-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Registrar Venta</h3>
              <p className="text-xs text-muted-foreground">{batch?.category_name || batch?.metal_name} · {weight?.toFixed(2)} t</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted transition-colors">
            <Icon name="X" size={16} color="var(--color-muted-foreground)" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4 max-h-[80vh] overflow-y-auto">
          {savedOk && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-50 border border-emerald-200">
              <Icon name="CheckCircle" size={14} color="#059669" />
              <p className="text-xs text-emerald-700" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Venta registrada correctamente</p>
            </div>
          )}

          {/* Pricing reference panel */}
          <PricingRefPanel batch={batch} />

          {/* Batch info */}
          <div className="grid grid-cols-2 gap-3 p-3 rounded-lg bg-muted/50 border border-border">
            <div>
              <p className="text-xs text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Precio Entrada</p>
              <p className="text-sm font-mono font-semibold text-foreground">{purchasePrice?.toLocaleString('es-ES', { maximumFractionDigits: 0 })} €/t</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Yield Estimado</p>
              <p className="text-sm font-mono font-semibold text-foreground">{batch?.yield_est || '—'}%</p>
            </div>
          </div>

          {/* Form fields */}
          <div className="space-y-3">
            {/* Sale price */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
                Precio de Venta (€/t) <span style={{ color: 'var(--color-error)' }}>*</span>
              </label>
              <input
                type="number"
                step="0.01"
                value={form?.sale_price_per_ton}
                onChange={e => handleChange('sale_price_per_ton', e?.target?.value)}
                onBlur={() => handleBlur('sale_price_per_ton')}
                placeholder="Ej: 4500"
                className="w-full px-3 py-2 text-sm bg-background border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                style={{
                  fontFamily: '"IBM Plex Mono", monospace',
                  borderColor: errors?.sale_price_per_ton ? 'var(--color-error)' : undefined,
                }}
              />
              <FieldError msg={errors?.sale_price_per_ton} />
            </div>

            {/* Real yield */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
                Yield Real (%)
              </label>
              <input
                type="number"
                step="0.1"
                value={form?.actual_yield_pct}
                onChange={e => handleChange('actual_yield_pct', e?.target?.value)}
                onBlur={() => handleBlur('actual_yield_pct')}
                placeholder="Ej: 87.5"
                className="w-full px-3 py-2 text-sm bg-background border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                style={{
                  fontFamily: '"IBM Plex Mono", monospace',
                  borderColor: errors?.actual_yield_pct ? 'var(--color-error)' : undefined,
                }}
              />
              <FieldError msg={errors?.actual_yield_pct} />
            </div>

            {/* Buyer name */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
                Comprador <span style={{ color: 'var(--color-error)' }}>*</span>
              </label>
              <input
                type="text"
                value={form?.buyer_name}
                onChange={e => handleChange('buyer_name', e?.target?.value)}
                onBlur={() => handleBlur('buyer_name')}
                placeholder="Nombre del comprador"
                className="w-full px-3 py-2 text-sm bg-background border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                style={{
                  fontFamily: '"IBM Plex Sans", sans-serif',
                  borderColor: errors?.buyer_name ? 'var(--color-error)' : undefined,
                }}
              />
              <FieldError msg={errors?.buyer_name} />
            </div>

            {/* Sold at date */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
                Fecha de Venta <span style={{ color: 'var(--color-error)' }}>*</span>
              </label>
              <input
                type="date"
                value={form?.sold_at}
                max={todayStr}
                onChange={e => handleChange('sold_at', e?.target?.value)}
                onBlur={() => handleBlur('sold_at')}
                className="w-full px-3 py-2 text-sm bg-background border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                style={{
                  fontFamily: '"IBM Plex Sans", sans-serif',
                  borderColor: errors?.sold_at ? 'var(--color-error)' : undefined,
                }}
              />
              <FieldError msg={errors?.sold_at} />
            </div>

            {/* Notes */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Notas</label>
                <span className="text-xs" style={{ color: form?.notes?.length > 500 ? 'var(--color-error)' : 'var(--color-muted-foreground)', fontFamily: '"IBM Plex Sans", sans-serif' }}>
                  {form?.notes?.length}/500
                </span>
              </div>
              <textarea
                value={form?.notes}
                onChange={e => handleChange('notes', e?.target?.value)}
                onBlur={() => handleBlur('notes')}
                placeholder="Observaciones de la venta..."
                rows={2}
                className="w-full px-3 py-2 text-sm bg-background border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent resize-none"
                style={{
                  fontFamily: '"IBM Plex Sans", sans-serif',
                  borderColor: errors?.notes ? 'var(--color-error)' : undefined,
                }}
              />
              <FieldError msg={errors?.notes} />
            </div>
          </div>

          {/* P&L preview */}
          {salePrice > 0 && (
            <div className={`p-3 rounded-lg border ${pnl >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>P&L Realizado</span>
                <span className={`text-base font-mono font-bold ${pnl >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {pnl >= 0 ? '+' : ''}{pnl?.toLocaleString('es-ES', { maximumFractionDigits: 0 })} €
                </span>
              </div>
              <div className="flex items-center justify-between mt-1">
                <span className="text-xs text-muted-foreground">Valor venta total</span>
                <span className="text-xs font-mono text-foreground">{totalSale?.toLocaleString('es-ES', { maximumFractionDigits: 0 })} €</span>
              </div>
            </div>
          )}

          {errors?._submit && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200">
              <Icon name="AlertCircle" size={14} color="#ef4444" />
              <p className="text-xs text-red-600" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>{errors?._submit}</p>
            </div>
          )}

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 text-sm font-medium text-foreground bg-muted border border-border rounded-lg hover:bg-muted/80 transition-colors" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Cancelar</button>
            <button type="submit" disabled={loading || savedOk} className="flex-1 px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-60 flex items-center justify-center gap-2" style={{ background: 'var(--color-accent)', fontFamily: '"IBM Plex Sans", sans-serif' }}>
              {loading && <Icon name="Loader2" size={14} color="white" className="animate-spin" />}
              {loading ? 'Guardando...' : savedOk ? 'Guardado ✓' : 'Confirmar Venta'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
